import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router';
import { useTitle } from 'react-use';

import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import { usePermissions } from '@/hooks/usePermissions';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import { BillingAddressFieldset } from '../components/fieldsets/BillingAddress';
import { CompanyNameFieldset } from '../components/fieldsets/CompanyName';
import { CouponFieldset } from '../components/fieldsets/Coupon';
import { InvoiceLanguageFieldset } from '../components/fieldsets/InvoiceLanguage';
import { InvoiceRecipientFieldset } from '../components/fieldsets/InvoiceRecipient';
import { PaymentMethodsFieldset } from '../components/fieldsets/PaymentMethods/PaymentMethods';
import { PlanFieldset } from '../components/fieldsets/Plan/Plan';
import { TaxRegionFieldset } from '../components/fieldsets/TaxRegion';
import { UsageLimitsFieldset } from '../components/fieldsets/UsageLimits';
import { useBilling } from '../hooks/useBilling';
import { useBillingState } from '../hooks/useBillingState';

export default function Billing() {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  useTitle(t('pages.billing_simple'));
  const { listStatus } = usePermissions();
  useSegments(PageName.BILLING);

  const features = useSelector(selectFeatureFlags);
  const billingState = useBillingState();
  const { canWriteBilling } = useBilling();
  const canReadBilling = useSelector((state: RootState) =>
    getPermissions(state, 'billing', actions.READ)
  );

  if (listStatus === 'pending') {
    return null;
  }

  if (!canReadBilling) {
    return <Navigate to={`/${slug}/account/information`} replace />;
  }

  // Google Marketplace and custom plans don't have Stripe billing features
  const isStripeAccount = billingState?.plan_type === 'stripe';

  return (
    <SettingsContent className="overflow-x-hidden pb-20">
      <h1 className="mb-2 px-1 pb-1 text-lg leading-none font-semibold tracking-tight text-foreground">
        {t('pages.billing_simple')}
      </h1>
      {/* A native fieldset so one flag disables every control the fieldsets render, saves included. */}
      <fieldset disabled={!canWriteBilling} className="min-w-0 space-y-4">
        <PlanFieldset />
        {isStripeAccount && (
          <>
            <PaymentMethodsFieldset />
            <CouponFieldset />
            <TaxRegionFieldset />
            <BillingAddressFieldset />
            <CompanyNameFieldset />
            <InvoiceRecipientFieldset />
            <InvoiceLanguageFieldset />
          </>
        )}
        {features?.usage_limits && <UsageLimitsFieldset />}
      </fieldset>
    </SettingsContent>
  );
}
